import React, { useState, useEffect } from "react";
import {
	Box,
	Button,
	Container,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	CircularProgress,
	Snackbar,
	Alert,
	Grid,
	Card,
	CardContent,
	Tabs,
	Tab,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Stack,
	IconButton,
	Collapse,
	TextField,
	InputAdornment,
} from "@mui/material";
import {
	Download as DownloadIcon,
	ExpandMore as ExpandMoreIcon,
	ExpandLess as ExpandLessIcon,
	Search as SearchIcon,
} from "@mui/icons-material";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useSelectedAcademicYear } from "../../hooks/useSelectedAcademicYear";
import AcademicYearSelector from "../../components/common/AcademicYearSelector";
import { filterReportSettingsForCumulative } from "../../utils/academicYear";
import * as XLSX from 'xlsx';

const BulkReports = () => {
	const { selectedAcademicYear, setSelectedAcademicYear } =
		useSelectedAcademicYear();

	const [activeTab, setActiveTab] = useState(0);
	const [teachersData, setTeachersData] = useState([]);
	const [schoolsData, setSchoolsData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
	const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
	const [expandedRows, setExpandedRows] = useState(new Set());
	const [searchQuery, setSearchQuery] = useState("");
	const [schoolSearchQuery, setSchoolSearchQuery] = useState("");
	const { user } = useAuth();

	const showSnackbar = (message, severity = "success") => {
		setSnackbar({ open: true, message, severity });
	};

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	const getMonthName = (month) => {
		const months = [
			"January", "February", "March", "April", "May", "June",
			"July", "August", "September", "October", "November", "December"
		];
		return months[month - 1] || month;
	};

	const fetchTeachersImplementationData = async () => {
		setLoading(true);
		try {
			// Fetch all teachers
			const { data: teachers, error: teachersError } = await supabase
				.from("profiles")
				.select(`
					id,
					full_name,
					email,
					school_id,
					schools (
						id,
						name
					)
				`)
				.eq("role", "ROLE_SCHOOL_TEACHER")
				.not("school_id", "is", null);

			if (teachersError) throw teachersError;

			// Fetch ALL report settings (we'll filter by month/year later)
			const { data: allReportSettings, error: allSettingsError } = await supabase
				.from("report_settings")
				.select("*")
				.eq("status", true);

			if (allSettingsError) throw allSettingsError;

			// Filter settings for the selected month/year
			const reportSettings = allReportSettings?.filter(s => 
				s.month === selectedMonth && s.year === selectedYear
			) || [];

			// Fetch all teacher grades
			const { data: teacherGrades, error: teacherGradesError } = await supabase
				.from("teacher_grades")
				.select(`
					profile_id,
					grade_id,
					grades:grade_id (
						name,
						parent_grade
					)
				`);

			if (teacherGradesError) throw teacherGradesError;

			// Fetch content progress for all teachers
			const startDate = new Date(selectedYear, selectedMonth - 1, 1);
			const endDate = new Date(selectedYear, selectedMonth, 0, 23, 59, 59);

			const { data: contentProgress, error: contentProgressError } = await supabase
				.from("teacher_content_progress")
				.select(`
					id,
					teacher_id,
					category_id,
					content_id,
					status,
					completed_at,
					course_categories:category_id (
						name,
						concept
					),
					course_contents:content_id (
						title,
						content_type
					)
				`)
				.eq("status", "completed")
				.eq("academic_year", selectedAcademicYear)
				.gte("completed_at", startDate.toISOString())
				.lte("completed_at", endDate.toISOString());

			if (contentProgressError) throw contentProgressError;

			const allCumulativeSettings = filterReportSettingsForCumulative(
				allReportSettings || [],
				selectedMonth,
				selectedYear,
				selectedAcademicYear
			).sort((a, b) => {
				if (a.year !== b.year) return a.year - b.year;
				return a.month - b.month;
			});

			// Group cumulative settings by parent_grade for quick lookup
			// Also group by full grade name in case some settings use full grade names
			const cumulativeSettingsByGrade = {};
			allCumulativeSettings?.forEach(setting => {
				const key = setting.parent_grade;
				if (key) {
					if (!cumulativeSettingsByGrade[key]) {
						cumulativeSettingsByGrade[key] = [];
					}
					cumulativeSettingsByGrade[key].push(setting);
				}
			});

			// Fetch ALL cumulative content progress upfront (much faster than per-teacher queries)
			const teacherIds = teachers?.map(t => t.id).filter(Boolean) || [];
			let allCumulativeProgress = [];
			
			if (teacherIds.length > 0) {
				// Use smaller batch size to avoid URL length limits (UUIDs are long)
				const batchSize = 50;
				const batches = [];
				for (let i = 0; i < teacherIds.length; i += batchSize) {
					batches.push(teacherIds.slice(i, i + batchSize));
				}

				// Fetch all batches in parallel
				const batchPromises = batches.map(batch => 
					supabase
						.from("teacher_content_progress")
						.select(`
							id,
							teacher_id,
							category_id,
							content_id,
							status,
							completed_at,
							course_categories:category_id (
								name,
								concept
							),
							course_contents:content_id (
								title,
								content_type
							)
						`)
						.in("teacher_id", batch)
						.eq("status", "completed")
						.eq("academic_year", selectedAcademicYear)
						.lte("completed_at", endDate.toISOString())
				);

				const batchResults = await Promise.all(batchPromises);
				
				// Check for errors
				for (const result of batchResults) {
					if (result.error) throw result.error;
					if (result.data) {
						allCumulativeProgress = allCumulativeProgress.concat(result.data);
					}
				}
			}

			// Group cumulative progress by teacher_id for quick lookup
			const cumulativeProgressByTeacher = {};
			allCumulativeProgress?.forEach(progress => {
				if (!cumulativeProgressByTeacher[progress.teacher_id]) {
					cumulativeProgressByTeacher[progress.teacher_id] = [];
				}
				cumulativeProgressByTeacher[progress.teacher_id].push(progress);
			});

			// Process data for each teacher (now using pre-fetched data)
			const teachersList = [];
			for (const teacher of teachers || []) {
				const schoolName = teacher.schools?.name || "N/A";

				// Get teacher's grade assignments
				const teacherGradeAssignments = teacherGrades?.filter(tg => tg.profile_id === teacher.id) || [];
				
				// Get teacher's content progress
				const teacherProgress = contentProgress?.filter(cp => cp.teacher_id === teacher.id) || [];

				// Calculate pre-game and game play counts
				let preGameCount = 0;
				let gameRulesCount = 0;

				teacherProgress.forEach(progress => {
					const contentTitle = progress.course_contents?.title?.toLowerCase() || "";
					const contentType = progress.course_contents?.content_type?.toLowerCase() || "";
					const categoryConcept = progress.course_categories?.concept?.toLowerCase() || "";

					// Check for Pre-Game Activity
					if (
						contentTitle.includes("pre") ||
						contentTitle.includes("introduction") ||
						categoryConcept.includes("pre") ||
						categoryConcept.includes("introduction")
					) {
						preGameCount++;
					}
					// Check for Game Play
					else if (
						contentTitle.includes("game") ||
						contentTitle.includes("rule") ||
						categoryConcept.includes("game") ||
						categoryConcept.includes("rule")
					) {
						gameRulesCount++;
					}
				});

				// Calculate videos required and implementation score
				let totalVideosRequired = 0;
				let totalCumulativeVideosRequired = 0;

				if (teacherGradeAssignments.length > 0) {
					// Track which parent grades we've already processed to avoid double-counting
					const processedParentGrades = new Set();
					let hasUsedAllSettings = false;
					
					for (const tg of teacherGradeAssignments) {
						const parentGrade = tg.grades?.parent_grade;
						const fullGradeName = tg.grades?.name;
						if (parentGrade && fullGradeName) {
							// Find setting for this grade and month/year
							let setting = reportSettings?.find(s => s.parent_grade === parentGrade);
							
							// Fallback: If no setting found for specific grade, try "ALL" settings
							if (!setting) {
								setting = reportSettings?.find(s => s.parent_grade === 'ALL');
							}
							
							if (setting) {
								totalVideosRequired += setting.number_of_videos_to_be_completed;
							}

							// Calculate cumulative videos required for this parent grade
							// Only process each parent grade once to avoid double-counting
							if (!processedParentGrades.has(parentGrade)) {
								processedParentGrades.add(parentGrade);
								
								// Get all cumulative settings for this parent grade
								let parentGradeSettings = cumulativeSettingsByGrade[parentGrade] || [];
								
								// Fallback: If no settings found for specific grade, try "ALL" settings
								// But only use "ALL" settings ONCE total for the teacher (not per parent grade)
								if (parentGradeSettings.length === 0) {
									if (!hasUsedAllSettings) {
										parentGradeSettings = cumulativeSettingsByGrade['ALL'] || [];
										hasUsedAllSettings = true;
									} else {
										// Already used "ALL" settings, skip this parent grade
										parentGradeSettings = [];
									}
								}
								
								if (parentGradeSettings.length > 0) {
									totalCumulativeVideosRequired += parentGradeSettings.reduce((sum, s) => 
										sum + s.number_of_videos_to_be_completed, 0
									);
								}
							}
						}
					}
				} else {
					// If no grade assignments, use all settings for the period
					totalVideosRequired = reportSettings?.reduce((sum, s) => sum + s.number_of_videos_to_be_completed, 0) || 0;
					// For cumulative, sum all cumulative settings
					totalCumulativeVideosRequired = allCumulativeSettings?.reduce((sum, s) => sum + s.number_of_videos_to_be_completed, 0) || 0;
				}

				const totalCompleted = preGameCount + gameRulesCount;
				const monthlyScore = totalVideosRequired > 0 
					? Math.min(100, (totalCompleted / totalVideosRequired) * 100)
					: 0;

				// Use pre-fetched cumulative progress (no database query needed!)
				const cumulativeProgress = cumulativeProgressByTeacher[teacher.id] || [];

				let cumulativePreGame = 0;
				let cumulativeGameRules = 0;

				// Filter cumulative progress to only include content from months that have report settings
				// This matches ReportGeneration's logic where it only counts content from active months
				cumulativeProgress.forEach(progress => {
					const completedDate = new Date(progress.completed_at);
					const completedMonth = completedDate.getMonth() + 1; // JavaScript months are 0-based
					const completedYear = completedDate.getFullYear();
					
					// Check if this completion is within any of the active months (months with settings)
					const isInActivePeriod = allCumulativeSettings.some(setting => 
						setting.month === completedMonth && setting.year === completedYear
					);
					
					// Only count content from months that have report settings
					if (isInActivePeriod) {
						const contentTitle = progress.course_contents?.title?.toLowerCase() || "";
						const categoryConcept = progress.course_categories?.concept?.toLowerCase() || "";

						// Prioritize content_title for more specific categorization
						// Check Pre-Game first to avoid "Pre-Game Activity" being categorized as Game Play
						if (
							contentTitle.includes("pre") ||
							contentTitle.includes("introduction")
						) {
							cumulativePreGame++;
						}
						else if (
							contentTitle.includes("game") ||
							contentTitle.includes("rule")
						) {
							cumulativeGameRules++;
						}
						else if (
							categoryConcept.includes("pre") ||
							categoryConcept.includes("introduction")
						) {
							cumulativePreGame++;
						}
						else if (
							categoryConcept.includes("game") ||
							categoryConcept.includes("rule")
						) {
							cumulativeGameRules++;
						}
						else {
							// Default to pre-game if we can't categorize
							cumulativePreGame++;
						}
					}
				});

				const cumulativeCompleted = cumulativePreGame + cumulativeGameRules;
				const cumulativeScore = totalCumulativeVideosRequired > 0 
					? Math.min(100, (cumulativeCompleted / totalCumulativeVideosRequired) * 100)
					: 0;

				teachersList.push({
					teacherName: teacher.full_name,
					email: teacher.email,
					schoolName: schoolName,
					grades: teacherGradeAssignments.map(tg => tg.grades?.name).filter(Boolean).join(", ") || "N/A",
					monthlyPreGame: preGameCount,
					monthlyGameRules: gameRulesCount,
					monthlyVideosRequired: totalVideosRequired,
					monthlyScore: monthlyScore,
					cumulativePreGame: cumulativePreGame,
					cumulativeGameRules: cumulativeGameRules,
					cumulativeVideosRequired: totalCumulativeVideosRequired,
					cumulativeScore: cumulativeScore,
				});
			}

			setTeachersData(teachersList);
		} catch (error) {
			console.error("Error fetching teachers data:", error);
			showSnackbar("Error fetching teachers implementation data", "error");
		} finally {
			setLoading(false);
		}
	};

	const fetchSchoolsImplementationData = async () => {
		setLoading(true);
		try {
			// Fetch all schools
			const { data: schools, error: schoolsError } = await supabase
				.from("schools")
				.select("id, name")
				.order("name");

			if (schoolsError) throw schoolsError;

			// Fetch ALL teachers upfront (much faster than per-school queries)
			const { data: allTeachers, error: allTeachersError } = await supabase
				.from("profiles")
				.select(`
					id,
					full_name,
					school_id
				`)
				.eq("role", "ROLE_SCHOOL_TEACHER")
				.not("school_id", "is", null);

			if (allTeachersError) throw allTeachersError;

			// Group teachers by school_id for quick lookup
			const teachersBySchool = {};
			allTeachers?.forEach(teacher => {
				if (!teachersBySchool[teacher.school_id]) {
					teachersBySchool[teacher.school_id] = [];
				}
				teachersBySchool[teacher.school_id].push(teacher);
			});

			// Fetch ALL report settings first (we'll filter by month/year later)
			const { data: allReportSettings, error: allSettingsError } = await supabase
				.from("report_settings")
				.select("*")
				.eq("status", true);

			if (allSettingsError) throw allSettingsError;

			const allCumulativeSettings = filterReportSettingsForCumulative(
				allReportSettings || [],
				selectedMonth,
				selectedYear,
				selectedAcademicYear
			).sort((a, b) => {
				if (a.year !== b.year) return a.year - b.year;
				return a.month - b.month;
			});

			// Calculate end date for cumulative progress query
			const endDate = new Date(selectedYear, selectedMonth, 0, 23, 59, 59);

			// Group cumulative settings by parent_grade
			const cumulativeSettingsByGrade = {};
			allCumulativeSettings?.forEach(setting => {
				if (!cumulativeSettingsByGrade[setting.parent_grade]) {
					cumulativeSettingsByGrade[setting.parent_grade] = [];
				}
				cumulativeSettingsByGrade[setting.parent_grade].push(setting);
			});

			// Fetch ALL teacher grades upfront
			const teacherIds = allTeachers?.map(t => t.id).filter(Boolean) || [];
			let allTeacherGrades = [];
			
			if (teacherIds.length > 0) {
				// Use smaller batch size to avoid URL length limits
				const batchSize = 50;
				const batches = [];
				for (let i = 0; i < teacherIds.length; i += batchSize) {
					batches.push(teacherIds.slice(i, i + batchSize));
				}

				const batchPromises = batches.map(batch =>
					supabase
						.from("teacher_grades")
						.select(`
							profile_id,
							grade_id,
							grades:grade_id (
								parent_grade
							)
						`)
						.in("profile_id", batch)
				);

				const batchResults = await Promise.all(batchPromises);
				for (const result of batchResults) {
					if (result.error) throw result.error;
					if (result.data) {
						allTeacherGrades = allTeacherGrades.concat(result.data);
					}
				}
			}

			// Group teacher grades by profile_id
			const teacherGradesByProfile = {};
			allTeacherGrades?.forEach(tg => {
				if (!teacherGradesByProfile[tg.profile_id]) {
					teacherGradesByProfile[tg.profile_id] = [];
				}
				teacherGradesByProfile[tg.profile_id].push(tg);
			});

			// Fetch ALL cumulative content progress upfront
			let allCumulativeProgress = [];
			
			if (teacherIds.length > 0) {
				// Use smaller batch size to avoid URL length limits
				const batchSize = 50;
				const batches = [];
				for (let i = 0; i < teacherIds.length; i += batchSize) {
					batches.push(teacherIds.slice(i, i + batchSize));
				}

				const batchPromises = batches.map(batch =>
					supabase
						.from("teacher_content_progress")
						.select(`
							id,
							teacher_id,
							category_id,
							content_id,
							status,
							completed_at,
							course_categories:category_id (
								name,
								concept
							),
							course_contents:content_id (
								title,
								content_type
							)
						`)
						.in("teacher_id", batch)
						.eq("status", "completed")
						.eq("academic_year", selectedAcademicYear)
						.lte("completed_at", endDate.toISOString())
				);

				const batchResults = await Promise.all(batchPromises);
				for (const result of batchResults) {
					if (result.error) throw result.error;
					if (result.data) {
						allCumulativeProgress = allCumulativeProgress.concat(result.data);
					}
				}
			}

			// Group cumulative progress by teacher_id
			const cumulativeProgressByTeacher = {};
			allCumulativeProgress?.forEach(progress => {
				if (!cumulativeProgressByTeacher[progress.teacher_id]) {
					cumulativeProgressByTeacher[progress.teacher_id] = [];
				}
				cumulativeProgressByTeacher[progress.teacher_id].push(progress);
			});

			// Process data for each school (now using pre-fetched data)
			const schoolsList = [];
			for (const school of schools || []) {
				const teachers = teachersBySchool[school.id] || [];

				// Calculate school implementation score
				let totalScore = 0;
				let teacherCount = 0;

				for (const teacher of teachers) {
					const teacherId = teacher.id;
					if (!teacherId) continue;

					// Use pre-fetched cumulative progress (no database query needed!)
					const contentProgress = cumulativeProgressByTeacher[teacherId] || [];

					// Calculate cumulative score
					let cumulativePreGame = 0;
					let cumulativeGameRules = 0;

					// Filter cumulative progress to only include content from months that have report settings
					// This matches ReportGeneration's logic where it only counts content from active months
					contentProgress.forEach(progress => {
						const completedDate = new Date(progress.completed_at);
						const completedMonth = completedDate.getMonth() + 1; // JavaScript months are 0-based
						const completedYear = completedDate.getFullYear();
						
						// Check if this completion is within any of the active months (months with settings)
						const isInActivePeriod = allCumulativeSettings.some(setting => 
							setting.month === completedMonth && setting.year === completedYear
						);
						
						// Only count content from months that have report settings
						if (isInActivePeriod) {
							const contentTitle = progress.course_contents?.title?.toLowerCase() || "";
							const categoryConcept = progress.course_categories?.concept?.toLowerCase() || "";

							// Prioritize content_title for more specific categorization
							// Check Pre-Game first to avoid "Pre-Game Activity" being categorized as Game Play
							if (
								contentTitle.includes("pre") ||
								contentTitle.includes("introduction")
							) {
								cumulativePreGame++;
							}
							else if (
								contentTitle.includes("game") ||
								contentTitle.includes("rule")
							) {
								cumulativeGameRules++;
							}
							else if (
								categoryConcept.includes("pre") ||
								categoryConcept.includes("introduction")
							) {
								cumulativePreGame++;
							}
							else if (
								categoryConcept.includes("game") ||
								categoryConcept.includes("rule")
							) {
								cumulativeGameRules++;
							}
							else {
								// Default to pre-game if we can't categorize
								cumulativePreGame++;
							}
						}
					});

					// Use pre-fetched teacher grades (no database query needed!)
					const teacherGrades = teacherGradesByProfile[teacherId] || [];

					let totalCumulativeVideosRequired = 0;
					if (teacherGrades.length > 0) {
						// Track which parent grades we've already processed to avoid double-counting
						const processedParentGrades = new Set();
						let hasUsedAllSettings = false;
						
						for (const tg of teacherGrades) {
							const parentGrade = tg.grades?.parent_grade;
							if (parentGrade) {
								// Only process each parent grade once to avoid double-counting
								if (!processedParentGrades.has(parentGrade)) {
									processedParentGrades.add(parentGrade);
									
									// Get all cumulative settings for this parent grade
									let parentGradeSettings = cumulativeSettingsByGrade[parentGrade] || [];
									
									// Fallback: If no settings found for specific grade, try "ALL" settings
									// But only use "ALL" settings ONCE total for the teacher (not per parent grade)
									if (parentGradeSettings.length === 0) {
										if (!hasUsedAllSettings) {
											parentGradeSettings = cumulativeSettingsByGrade['ALL'] || [];
											hasUsedAllSettings = true;
										} else {
											// Already used "ALL" settings, skip this parent grade
											parentGradeSettings = [];
										}
									}
									
									if (parentGradeSettings.length > 0) {
										totalCumulativeVideosRequired += parentGradeSettings.reduce((sum, s) => 
											sum + s.number_of_videos_to_be_completed, 0
										);
									}
								}
							}
						}
					}

					const cumulativeCompleted = cumulativePreGame + cumulativeGameRules;
					const cumulativeScore = totalCumulativeVideosRequired > 0 
						? Math.min(100, (cumulativeCompleted / totalCumulativeVideosRequired) * 100)
						: 0;

					totalScore += cumulativeScore;
					teacherCount++;
				}

				const averageScore = teacherCount > 0 ? totalScore / teacherCount : 0;

				schoolsList.push({
					schoolName: school.name,
					teacherCount: teacherCount,
					implementationScore: averageScore,
				});
			}

			setSchoolsData(schoolsList);
		} catch (error) {
			console.error("Error fetching schools data:", error);
			showSnackbar("Error fetching schools implementation data", "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (activeTab === 0) {
			fetchTeachersImplementationData();
		} else {
			fetchSchoolsImplementationData();
		}
	}, [activeTab, selectedMonth, selectedYear, selectedAcademicYear]);

	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
		setExpandedRows(new Set()); // Reset expanded rows when switching tabs
	};

	const handleRowExpand = (teacherId) => {
		const newExpanded = new Set(expandedRows);
		if (newExpanded.has(teacherId)) {
			newExpanded.delete(teacherId);
		} else {
			newExpanded.add(teacherId);
		}
		setExpandedRows(newExpanded);
	};

	const handleDownloadExcel = (type) => {
		try {
			showSnackbar("Generating Excel...", "info");

			const workbook = XLSX.utils.book_new();

			if (type === "teachers") {
				const teachersDataForExcel = [];
				teachersDataForExcel.push([
					"Teacher Name",
					"Email",
					"School Name",
					"Grades",
					"Monthly Pre-Game Activity",
					"Monthly Game Play",
					"Monthly Videos Required",
					"Monthly Score %",
					"Cumulative Pre-Game Activity",
					"Cumulative Game Play",
					"Cumulative Videos Required",
					"Cumulative Score %"
				]);

				teachersData.forEach(teacher => {
					teachersDataForExcel.push([
						teacher.teacherName,
						teacher.email,
						teacher.schoolName,
						teacher.grades,
						teacher.monthlyPreGame,
						teacher.monthlyGameRules,
						teacher.monthlyVideosRequired,
						teacher.monthlyScore.toFixed(2),
						teacher.cumulativePreGame,
						teacher.cumulativeGameRules,
						teacher.cumulativeVideosRequired,
						teacher.cumulativeScore.toFixed(2)
					]);
				});

				const worksheet = XLSX.utils.aoa_to_sheet(teachersDataForExcel);
				worksheet['!cols'] = [
					{ wch: 25 }, { wch: 30 }, { wch: 30 }, { wch: 20 },
					{ wch: 25 }, { wch: 20 }, { wch: 25 }, { wch: 20 },
					{ wch: 30 }, { wch: 25 }, { wch: 30 }, { wch: 25 }
				];
				XLSX.utils.book_append_sheet(workbook, worksheet, "Teachers Implementation");
			} else {
				const schoolsDataForExcel = [];
				schoolsDataForExcel.push([
					"School Name",
					"Number of Teachers",
					"Implementation Score %"
				]);

				schoolsData.forEach(school => {
					schoolsDataForExcel.push([
						school.schoolName,
						school.teacherCount,
						school.implementationScore.toFixed(2)
					]);
				});

				const worksheet = XLSX.utils.aoa_to_sheet(schoolsDataForExcel);
				worksheet['!cols'] = [
					{ wch: 40 }, { wch: 20 }, { wch: 25 }
				];
				XLSX.utils.book_append_sheet(workbook, worksheet, "Schools Implementation");
			}

			const monthYear = `${getMonthName(selectedMonth)}_${selectedYear}`;
			const filename = type === "teachers" 
				? `Teachers_Implementation_List_${monthYear}.xlsx`
				: `Schools_Implementation_List_${monthYear}.xlsx`;

			XLSX.writeFile(workbook, filename);
			showSnackbar("Excel file downloaded successfully", "success");
		} catch (error) {
			console.error("Error generating Excel:", error);
			showSnackbar("Error generating Excel", "error");
		}
	};

	const getScoreColor = (score) => {
		if (score >= 90) return { bg: '#e8f5e8', border: '#4caf50' };
		if (score >= 80) return { bg: '#e3f2fd', border: '#2196f3' };
		if (score >= 60) return { bg: '#fffde7', border: '#ffeb3b' };
		if (score >= 50) return { bg: '#fff3e0', border: '#ff9800' };
		return { bg: '#ffebee', border: '#f44336' };
	};

	return (
		<Container maxWidth="xl" sx={{ py: 4 }}>
			<Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
				Bulk Reports
			</Typography>
			<Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
				View implementation data for all teachers and schools.
			</Typography>

			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} md={3}>
							<AcademicYearSelector
								value={selectedAcademicYear}
								onChange={setSelectedAcademicYear}
								sx={{ width: "100%" }}
								minWidth={undefined}
							/>
						</Grid>
						<Grid item xs={12} md={3}>
							<FormControl fullWidth>
								<InputLabel>Month</InputLabel>
								<Select
									value={selectedMonth}
									label="Month"
									onChange={(e) => setSelectedMonth(e.target.value)}
								>
									{Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
										<MenuItem key={month} value={month}>
											{getMonthName(month)}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} md={3}>
							<FormControl fullWidth>
								<InputLabel>Year</InputLabel>
								<Select
									value={selectedYear}
									label="Year"
									onChange={(e) => setSelectedYear(e.target.value)}
								>
									{Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
										<MenuItem key={year} value={year}>
											{year}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
					</Grid>
				</CardContent>
			</Card>

			<Card>
				<CardContent>
					<Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
						<Tabs value={activeTab} onChange={handleTabChange}>
							<Tab label="Teachers Implementation List" />
							<Tab label="School Implementation List" />
						</Tabs>
					</Box>

					{loading ? (
						<Box display="flex" justifyContent="center" p={4}>
							<CircularProgress />
						</Box>
					) : (
						<>
						{activeTab === 0 ? (
							<>
								<Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
									<TextField
										placeholder="Search by teacher name or school name..."
										variant="outlined"
										size="small"
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										sx={{ width: 400 }}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon />
												</InputAdornment>
											),
										}}
									/>
									<Button
										variant="outlined"
										startIcon={<DownloadIcon />}
										onClick={() => handleDownloadExcel("teachers")}
										disabled={teachersData.length === 0}
									>
										Download Excel
									</Button>
								</Stack>
								<TableContainer component={Paper} sx={{ maxWidth: '100%' }}>
									<Table size="small" sx={{ tableLayout: 'auto' }}>
										<TableHead>
											<TableRow>
												<TableCell padding="checkbox" sx={{ width: 50 }}></TableCell>
												<TableCell>Teacher Name</TableCell>
												<TableCell>School Name</TableCell>
												<TableCell align="center" sx={{ width: 150 }}>Cumulative Score %</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{(() => {
												// Filter and sort teachers data
												let filteredTeachers = teachersData.filter(teacher => {
													const query = searchQuery.toLowerCase();
													return (
														teacher.teacherName.toLowerCase().includes(query) ||
														teacher.schoolName.toLowerCase().includes(query)
													);
												});
												
												// Sort by school name first, then by teacher name
												filteredTeachers.sort((a, b) => {
													const schoolCompare = a.schoolName.localeCompare(b.schoolName);
													if (schoolCompare !== 0) return schoolCompare;
													return a.teacherName.localeCompare(b.teacherName);
												});
												
												return filteredTeachers.map((teacher, index) => {
													const scoreColor = getScoreColor(teacher.cumulativeScore);
													const teacherId = `${teacher.email}-${teacher.teacherName}`;
													const isExpanded = expandedRows.has(teacherId);
													return (
														<React.Fragment key={teacherId}>
															<TableRow 
																sx={{
																	backgroundColor: teacher.cumulativeScore < 50 ? '#ffebee' : 'inherit',
																	'&:hover': {
																		backgroundColor: teacher.cumulativeScore < 50 ? '#ffcdd2' : '#f5f5f5',
																		cursor: 'pointer'
																	}
																}}
																onClick={() => handleRowExpand(teacherId)}
															>
																<TableCell padding="checkbox">
																	<IconButton
																		size="small"
																		onClick={(e) => {
																			e.stopPropagation();
																			handleRowExpand(teacherId);
																		}}
																	>
																		{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
																	</IconButton>
																</TableCell>
																<TableCell>{teacher.teacherName}</TableCell>
																<TableCell>{teacher.schoolName}</TableCell>
																<TableCell align="center">
																	<Box
																		sx={{
																			display: 'inline-block',
																			padding: '4px 12px',
																			borderRadius: '16px',
																			backgroundColor: scoreColor.bg,
																			border: `1px solid ${scoreColor.border}`,
																			color: '#000',
																			fontWeight: 500
																		}}
																	>
																		{teacher.cumulativeScore.toFixed(2)}%
																	</Box>
																</TableCell>
															</TableRow>
															<TableRow>
																<TableCell 
																	colSpan={4} 
																	sx={{ 
																		padding: 0,
																		borderBottom: isExpanded ? '1px solid rgba(224, 224, 224, 1)' : 'none'
																	}}
																>
																	<Collapse in={isExpanded} timeout="auto" unmountOnExit>
																		<Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
																			<Grid container spacing={2}>
																				<Grid item xs={12} md={6}>
																					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
																						Basic Information
																					</Typography>
																					<Box sx={{ pl: 1 }}>
																						<Typography variant="body2"><strong>Email:</strong> {teacher.email}</Typography>
																						<Typography variant="body2"><strong>Grades:</strong> {teacher.grades}</Typography>
																					</Box>
																				</Grid>
																				<Grid item xs={12} md={6}>
																					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
																						Monthly Implementation ({getMonthName(selectedMonth)} {selectedYear})
																					</Typography>
																					<Box sx={{ pl: 1 }}>
																						<Typography variant="body2"><strong>Pre-Game Activity:</strong> {teacher.monthlyPreGame}</Typography>
																						<Typography variant="body2"><strong>Game Play:</strong> {teacher.monthlyGameRules}</Typography>
																						<Typography variant="body2"><strong>Videos Required:</strong> {teacher.monthlyVideosRequired}</Typography>
																						<Typography variant="body2"><strong>Score:</strong> {teacher.monthlyScore.toFixed(2)}%</Typography>
																					</Box>
																				</Grid>
																				<Grid item xs={12} md={6}>
																					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
																						Cumulative Implementation
																					</Typography>
																					<Box sx={{ pl: 1 }}>
																						<Typography variant="body2"><strong>Pre-Game Activity:</strong> {teacher.cumulativePreGame}</Typography>
																						<Typography variant="body2"><strong>Game Play:</strong> {teacher.cumulativeGameRules}</Typography>
																						<Typography variant="body2"><strong>Videos Required:</strong> {teacher.cumulativeVideosRequired}</Typography>
																						<Typography variant="body2"><strong>Score:</strong> {teacher.cumulativeScore.toFixed(2)}%</Typography>
																					</Box>
																				</Grid>
																			</Grid>
																		</Box>
																	</Collapse>
																</TableCell>
															</TableRow>
														</React.Fragment>
													);
												});
											})()}
											</TableBody>
										</Table>
									</TableContainer>
								</>
							) : (
								<>
									<Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
										<TextField
											placeholder="Search by school name..."
											variant="outlined"
											size="small"
											value={schoolSearchQuery}
											onChange={(e) => setSchoolSearchQuery(e.target.value)}
											sx={{ width: 400 }}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<SearchIcon />
													</InputAdornment>
												),
											}}
										/>
										<Button
											variant="outlined"
											startIcon={<DownloadIcon />}
											onClick={() => handleDownloadExcel("schools")}
											disabled={schoolsData.length === 0}
										>
											Download Excel
										</Button>
									</Stack>
									<TableContainer component={Paper} sx={{ maxWidth: '100%' }}>
										<Table size="small" sx={{ tableLayout: 'auto' }}>
											<TableHead>
												<TableRow>
													<TableCell>School Name</TableCell>
													<TableCell align="center" sx={{ width: 150 }}>Number of Teachers</TableCell>
													<TableCell align="center" sx={{ width: 150 }}>Implementation Score %</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{(() => {
													// Filter and sort schools data
													let filteredSchools = schoolsData.filter(school => {
														const query = schoolSearchQuery.toLowerCase();
														return school.schoolName.toLowerCase().includes(query);
													});
													
													// Sort by school name
													filteredSchools.sort((a, b) => 
														a.schoolName.localeCompare(b.schoolName)
													);
													
													return filteredSchools.map((school, index) => {
														const scoreColor = getScoreColor(school.implementationScore);
														return (
															<TableRow 
																key={index}
																sx={{
																	backgroundColor: school.implementationScore < 50 ? '#ffebee' : 'inherit',
																	'&:hover': {
																		backgroundColor: school.implementationScore < 50 ? '#ffcdd2' : '#f5f5f5'
																	}
																}}
															>
																<TableCell>{school.schoolName}</TableCell>
																<TableCell align="center">{school.teacherCount}</TableCell>
																<TableCell align="center">
																	<Box
																		sx={{
																			display: 'inline-block',
																			padding: '4px 12px',
																			borderRadius: '16px',
																			backgroundColor: scoreColor.bg,
																			border: `1px solid ${scoreColor.border}`,
																			color: '#000',
																			fontWeight: 500
																		}}
																	>
																		{school.implementationScore.toFixed(2)}%
																	</Box>
																</TableCell>
															</TableRow>
														);
													});
												})()}
											</TableBody>
										</Table>
									</TableContainer>
								</>
							)}
						</>
					)}
				</CardContent>
			</Card>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default BulkReports;

